import * as yup from 'yup'
import { ICustomerWithPricing } from '@/supabase/Customer/customer.interface'

// Form values interface for customer pricing form
export interface ICustomerPricingFormValues {
  name: string
  largeIceCubePriceTHB: number
  smallIceCubePriceTHB: number
  crushedIcePriceTHB: number
}

// Component prop interfaces
export interface IDefaultPricingCardProps {
  defaultCustomer: ICustomerWithPricing | null
  onEdit: (
    customer: ICustomerWithPricing,
    formData?: ICustomerPricingFormValues
  ) => void
  loading?: boolean
}

export interface ICustomerPricingFormProps {
  visible: boolean
  customer?: ICustomerWithPricing | null
  onClose: () => void
  onSuccess: () => void
  inline?: boolean
}

export interface IRegularCustomerTableProps {
  customers: ICustomerWithPricing[]
  onEditCustomer: (
    customer: ICustomerWithPricing,
    formData?: ICustomerPricingFormValues
  ) => void
  onCreateCustomer: (formData?: ICustomerPricingFormValues) => void
  onUpdateRanking: (
    customerId: string,
    direction: 'up' | 'down'
  ) => void
  onDeleteCustomer: (customerId: string) => void
  loading?: boolean
}

export interface IPageState {
  isFormVisible: boolean
  selectedCustomer: ICustomerWithPricing | null
  isLoading: boolean
}

// Validation schema with English error messages
export const customerPricingValidationSchema = yup.object({
  name: yup
    .string()
    .required('Please enter customer name')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must not exceed 100 characters'),
  largeIceCubePriceTHB: yup
    .number()
    .transform((value, originalValue) => 
      originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value
    )
    .required('Please enter large ice cube price')
    .min(0, 'Price cannot be negative')
    .max(1000000, 'Maximum price is 1,000,000 THB')
    .typeError('Please enter numbers only'),
  smallIceCubePriceTHB: yup
    .number()
    .transform((value, originalValue) => 
      originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value
    )
    .required('Please enter small ice cube price')
    .min(0, 'Price cannot be negative')
    .max(1000000, 'Maximum price is 1,000,000 THB')
    .typeError('Please enter numbers only'),
  crushedIcePriceTHB: yup
    .number()
    .transform((value, originalValue) => 
      originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value
    )
    .required('Please enter crushed ice price')
    .min(0, 'Price cannot be negative')
    .max(1000000, 'Maximum price is 1,000,000 THB')
    .typeError('Please enter numbers only'),
})

// Default customer constants
export const DEFAULT_CUSTOMER_ID = 'default'
export const DEFAULT_CUSTOMER_NAME = 'Default Customer Pricing'

// Helper type for form initial values
export const getInitialFormValues = (
  customer?: ICustomerWithPricing | null
): ICustomerPricingFormValues => {
  return {
    name: customer?.name || '',
    largeIceCubePriceTHB: customer?.pricing?.large_ice_cube_price_thb || 0,
    smallIceCubePriceTHB: customer?.pricing?.small_ice_cube_price_thb || 0,
    crushedIcePriceTHB: customer?.pricing?.crushed_ice_price_thb || 0,
  }
}

// Type guard for default customer
export const isDefaultCustomer = (customer: ICustomerWithPricing): boolean => {
  return customer.id === DEFAULT_CUSTOMER_ID && customer.type === 'REGULAR'
}
